export const ONBOARD_OPERATING_MODES = [
  {
    id: "analyzeEmployees",
    label: "Analyze existing employees",
    description: "APas and CPas for current staff; enables Compatibility tab for customer admin/HR roles.",
    defaultTestTypeNames: ["A-PAS", "C-PAS - Ideal", "Employability Factors"],
  },
  {
    id: "assessCandidates",
    label: "Assess candidates (job match)",
    description: "Job-match workflows for candidates; enables Job Match tab for customer admin/HR roles.",
    defaultTestTypeNames: ["A/C PAS Applicant", "C-PAS - Ideal", "Employability Factors"],
  },
] as const;

export type OnboardTestTypeOption = {
  id: number;
  name: string;
  testTypeName: string;
};

export function defaultTestTypeIdsForModes(
  testTypes: OnboardTestTypeOption[],
  operatingModeIds: string[]
): number[] {
  const nameSet = new Set<string>();
  for (const modeId of operatingModeIds) {
    const mode = ONBOARD_OPERATING_MODES.find((m) => m.id === modeId);
    mode?.defaultTestTypeNames.forEach((n) => nameSet.add(n.toLowerCase()));
  }

  const ids = new Set<number>();
  for (const tt of testTypes) {
    const names = [tt.name, tt.testTypeName].map((n) => n.trim().toLowerCase());
    if (names.some((n) => nameSet.has(n)))
      ids.add(tt.id);
  }
  return Array.from(ids);
}

export type OnboardCttSelection = {
  name: string;
  testTypeName: string;
};

export function buildTestTypeAlignmentWarnings(
  selections: OnboardCttSelection[],
  operatingModeIds: string[]
): string[] {
  const hasName = (...candidates: string[]) =>
    selections.some((t) =>
      candidates.some(
        (c) =>
          t.name.localeCompare(c, undefined, { sensitivity: "accent" }) === 0 ||
          t.testTypeName.localeCompare(c, undefined, { sensitivity: "accent" }) === 0
      )
    );

  const warnings: string[] = [];
  if (operatingModeIds.includes("analyzeEmployees") && (!hasName("A-PAS") || !hasName("C-PAS - Ideal"))) {
    warnings.push("Analyze existing employees is selected but A-PAS and/or C-PAS - Ideal are not included.");
  }
  if (operatingModeIds.includes("assessCandidates") && !hasName("A/C PAS Applicant")) {
    warnings.push("Assess candidates is selected but A/C PAS Applicant is not included.");
  }
  return warnings;
}

export function onboardSelectionsFromStarter(
  testTypes: OnboardTestTypeOption[],
  selectedIds: number[]
): OnboardCttSelection[] {
  return testTypes
    .filter((t) => selectedIds.includes(t.id))
    .map((t) => ({ name: t.name, testTypeName: t.testTypeName }));
}
